import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldWarning, ArrowRight, WarningCircle, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useFraudSummary } from "@/hooks/useFraudSummary";
import FraudRow from "./FraudRow";

/**
 * Compact Anti-Fraud widget — collapsed by default, click card to expand
 * the 5-latest list. Per the redesign mockup: orange border-left, small
 * shield icon in soft-bg circle, count on the right, "7 hari terakhir"
 * caption. Expanded view still shows the same rows + link to Audit Trail.
 */
export default function FraudAttemptsWidget() {
  const { items, count7d, loading } = useFraudSummary();
  const [expanded, setExpanded] = useState(false);
  const isHighAlert = count7d >= 5;

  return (
    <div
      className={`rounded-xl border-l-4 ${isHighAlert ? "border-l-red-500" : "border-l-primary"} border border-border bg-card`}
      data-testid="fraud-attempts-widget"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-4 flex items-center gap-4 text-left hover:bg-secondary/30 transition-colors rounded-xl"
        data-testid="fraud-attempts-toggle"
      >
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isHighAlert ? "bg-red-100 text-red-600" : "bg-orange-100 text-primary"}`}>
          <ShieldWarning size={22} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Anti-Fraud</div>
          <div className="font-black text-base leading-tight">Upaya SPB Palsu</div>
        </div>
        <div className="text-right">
          <div
            className={`text-3xl font-black leading-none ${isHighAlert ? "text-red-600" : "text-foreground"}`}
            data-testid="fraud-attempts-count-7d"
          >
            {count7d}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">7 hari terakhir</div>
        </div>
        <div className="text-muted-foreground shrink-0">
          {expanded ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border" data-testid="fraud-attempts-details">
          {isHighAlert && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex items-center gap-1 font-semibold">
              <WarningCircle size={14} weight="fill" />
              Perhatian: aktivitas SPB palsu di atas normal
            </div>
          )}
          <div className="p-2">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">memuat…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-4xl mb-2 opacity-60">✓</div>
                <div className="text-sm text-muted-foreground">Belum ada upaya SPB palsu terdeteksi</div>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((it) => <FraudRow key={it.id} item={it} />)}
              </ul>
            )}
          </div>
          <Link
            to="/audit?action=spb.verify_failed"
            className="p-3 border-t border-border text-center text-xs font-bold uppercase tracking-wider hover:bg-secondary/50 flex items-center justify-center gap-1"
            data-testid="fraud-attempts-view-all"
          >
            Lihat semua di Audit Trail <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}
    </div>
  );
}
